import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { Alert, Button, Card, CardContent, Stack, Typography } from "@mui/material";
import Papa from "papaparse";
import { useCallback, useRef, useState } from "react";
import type { ParsedRow } from "./payment-import-logic";

type UploadCardProps = {
	onParsed: (rows: ParsedRow[]) => void;
};

export default function UploadCard({ onParsed }: UploadCardProps) {
	const fileInputRef = useRef<HTMLInputElement | null>(null);
	const [fileName, setFileName] = useState<string | null>(null);
	const [parseError, setParseError] = useState<string | null>(null);
	const [parsing, setParsing] = useState(false);

	const handleFileChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const file = event.target.files?.[0];
			if (!file) return;

			setFileName(file.name);
			setParsing(true);
			setParseError(null);

			Papa.parse(file, {
				header: true,
				skipEmptyLines: true,
				complete: (result: any) => {
					setParsing(false);
					if (result.errors && result.errors.length) {
						setParseError(result.errors[0].message || "Fehler beim Parsen der CSV-Datei");
						return;
					}
					onParsed(result.data as ParsedRow[]);
				},
				error: (error: any) => { setParsing(false); setParseError(error?.message ?? String(error)); },
			});

			event.target.value = "";
		},
		[onParsed],
	);

	return (
		<Card>
			<CardContent>
				<Stack spacing={2}>
					<Typography variant="h6">PayPal CSV hochladen</Typography>
					<Typography variant="body2">
						Wähle die von PayPal exportierte CSV-Datei aus. Die Daten werden
						lokal geparst und derzeit nur in der Konsole ausgegeben.
					</Typography>
					<input ref={fileInputRef} type="file" accept=".csv,text/csv" onChange={handleFileChange} style={{ display: "none" }} />
					<Button variant="contained" startIcon={<CloudUploadIcon />} onClick={() => fileInputRef.current?.click()} disableElevation disabled={parsing}>
						Datei auswählen
					</Button>
					{fileName && (
						<Typography variant="body2" color="text.secondary">Ausgewählte Datei: {fileName}</Typography>
					)}
					{parsing && <Typography variant="body2">CSV wird geladen...</Typography>}
					{parseError && <Alert severity="error">{parseError}</Alert>}
				</Stack>
			</CardContent>
		</Card>
	);
}
